// Reports → Daily summary (N-27, F-ANL-07): one business day at a glance — the same figures as the email owners get
// after each day closes, so the email's "Open the report" lands here on the same numbers. Owners only.
//
//   the headline figures   net takings, gross sales, sessions, hours played, occupancy, players — each against the same
//                          weekday last week
//   highlights             what needs a look (the day not closed, a cash variance over the alert amount, refunds well
//                          above usual, a shift still open, a station offline a long time, stock alerts), the best venue
//                          and net takings against the seven-day average
//   venues                 the organisation's: each venue on its own business day, most net takings first
//   the sections           takings and how they were paid, play, food and drink, wallets and credit, cash, players
//
// Every figure is the server's (`GET …/reports/daily-summary`); this lays them out in the report kit.

import type { DailySummary, DailySummaryChange, DailySummaryFlag, DailySummaryKpis, DailySummaryVenue, Money } from '@clavian/api-client';
import { dailySummaryFlagWording } from '@clavian/domain/daily-summary';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Badge, StatCard, type StatDelta, Table, type TableColumn } from '@clavian/ui-web';
import type { ReactNode } from 'react';

import { HeadlineMoney, KpiFigure, ReportCard, ReportGrid, ReportKpis, ReportNote, ReportPage, ReportRow, ReportRows } from './report-kit';
import { useDailySummary } from './queries';
import { type Format, NO_DATA, percentOf, TabPending } from './tab-kit';

import './daily-summary.css';

/** The summary of a scope and day: the organisation's (`venueId` undefined) or one venue's. */
export function DailySummaryTab({ organizationId, venueId, day }: { readonly organizationId: string; readonly venueId: string | undefined; readonly day: string | undefined }): ReactNode {
  const { t } = useTranslation('console');
  const summary = useDailySummary(organizationId, venueId, day, true);
  if (summary.data === undefined) return <TabPending query={summary} unavailable={t('reports.daily.unavailable')} forbidden={t('reports.daily.notPermitted')} />;
  return <Summary summary={summary.data} />;
}

/** "Monday, 5 October" — a business day is a calendar date: formatted in UTC so no timezone moves it. */
function dateWords(format: Format, day: string, options: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat(format.locale, { ...options, timeZone: 'UTC' }).format(new Date(`${day}T12:00:00Z`));
}

/** A change against last week as the StatCard's delta chip. */
function deltaOfChange(format: Format, change: DailySummaryChange | null | undefined, caption: string, upIsGood = true): { delta?: StatDelta } {
  if (change === null || change === undefined) return {};
  const value = change.basisPoints === null ? NO_DATA : format.number(change.basisPoints / 10_000, { style: 'percent', maximumFractionDigits: change.basisPoints < 1_000 ? 1 : 0 });
  const tone = change.direction === 'flat' ? 'neutral' : (change.direction === 'up') === upIsGood ? 'ok' : 'danger';
  return { delta: { value, trend: change.direction, tone, caption } };
}

/** "▲ 12%", "▼ 3.5%", "–": a change in a table cell or a sentence. */
function arrowWords(format: Format, change: DailySummaryChange | null | undefined): string {
  if (change === null || change === undefined || change.direction === 'flat') return '–';
  const arrow = change.direction === 'up' ? '▲' : '▼';
  return change.basisPoints === null ? arrow : `${arrow} ${format.number(change.basisPoints / 10_000, { style: 'percent', maximumFractionDigits: change.basisPoints < 1_000 ? 1 : 0 })}`;
}

function hoursWords(t: (key: string, values?: Record<string, unknown>) => string, format: Format, seconds: number): string {
  return t('reports.daily.duration', { hours: format.number(Math.floor(seconds / 3600)), minutes: String(Math.round((seconds % 3600) / 60)) });
}

function Summary({ summary }: { readonly summary: DailySummary }): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const kpis: DailySummaryKpis | undefined = summary.totals ?? summary.venues[0]?.kpis;
  const weekday = dateWords(format, summary.businessDay, { weekday: 'long' });
  const caption = t('reports.daily.vsLastWeek', { weekday });
  const money = (trend: string) => summary.moneyTrends.find((one) => one.metric === trend);
  const count = (trend: string) => summary.trends.find((one) => one.metric === trend);

  return (
    <ReportPage>
      <p className="console-ds-when console-subtle">
        <span className="console-ds-when__date">{dateWords(format, summary.businessDay, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</span>
        {' · '}
        {summary.sentAt === null
          ? t('reports.daily.readNow')
          : `${t('reports.daily.sent', { when: format.dateTime(summary.sentAt, { date: 'medium', time: 'short' }) })} ${summary.trigger === 'deadline' ? t('reports.daily.triggerDeadline') : t('reports.daily.triggerClosed')}`}
      </p>
      {summary.partial ? <ReportNote>{t('reports.partial')}</ReportNote> : null}
      {kpis === undefined ? null : (
        <>
          <ReportKpis label={t('reports.daily.kpis')} columns={3}>
            <StatCard label={t('reports.daily.netTakings')} value={<HeadlineMoney format={format} value={kpis.takings.netTakings} />} {...deltaOfChange(format, money('netTakings')?.vsLastWeek, caption)} />
            <StatCard label={t('reports.daily.grossSales')} value={<HeadlineMoney format={format} value={kpis.takings.grossSales} />} hint={t('reports.daily.grossSalesHint', { tax: format.money(kpis.takings.tax) })} />
            <StatCard label={t('reports.daily.sessions')} value={<KpiFigure value={kpis.play.sessions} format={(value) => format.number(value)} />} {...deltaOfChange(format, count('sessions')?.vsLastWeek, caption)} />
            <StatCard label={t('reports.daily.hoursPlayed')} value={<span className="console-num">{hoursWords(t, format, kpis.play.playSeconds)}</span>} {...deltaOfChange(format, count('playSeconds')?.vsLastWeek, caption)} />
            <StatCard label={t('reports.daily.occupancy')} value={<span className="console-num">{percentOf(format, kpis.play.utilisationBasisPoints)}</span>} {...deltaOfChange(format, count('utilisation')?.vsLastWeek, caption)} />
            <StatCard
              label={t('reports.daily.players')}
              value={<KpiFigure value={kpis.players.unique} format={(value) => format.number(value)} />}
              hint={t('reports.daily.playersHint', { count: kpis.players.new })}
              {...deltaOfChange(format, count('uniquePlayers')?.vsLastWeek, caption)}
            />
          </ReportKpis>
          <Highlights summary={summary} />
          {summary.kind === 'organization' && summary.venues.length > 0 ? <Venues summary={summary} /> : null}
          <Sections kpis={kpis} />
        </>
      )}
    </ReportPage>
  );
}

function flagWords(t: (key: string, values?: Record<string, unknown>) => string, format: Format, flag: DailySummaryFlag): string {
  const { key, amount } = dailySummaryFlagWording(flag);
  return t(`reports.daily.flag.${key}`, { venue: flag.venueName, amount: amount === null ? '' : format.money(amount), count: flag.count ?? 0 });
}

function Highlights({ summary }: { readonly summary: DailySummary }): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const best = summary.venues.find((venue) => venue.venueId === summary.bestVenueId);
  const average = summary.moneyTrends.find((one) => one.metric === 'netTakings');
  return (
    <ReportCard title={t('reports.daily.highlights')} description={t('reports.daily.highlightsHint')}>
      <ul className="console-ds-highlights" aria-label={t('reports.daily.highlights')}>
        {summary.flags.map((flag) => (
          <li key={`${flag.kind}:${flag.venueId}`} className="console-ds-highlight" data-tone="attention">
            {flagWords(t, format, flag)}
          </li>
        ))}
        {summary.flags.length === 0 ? (
          <li className="console-ds-highlight" data-tone="quiet">
            {t('reports.daily.allClear')}
          </li>
        ) : null}
        {summary.kind === 'organization' && best !== undefined ? (
          <li className="console-ds-highlight" data-tone="good">
            {t('reports.daily.best', { venue: best.name, amount: format.money(best.kpis.takings.netTakings) })}
          </li>
        ) : null}
        {average?.average !== null && average?.average !== undefined && average.vsAverage !== null ? (
          <li className="console-ds-highlight" data-tone="quiet">
            {t('reports.daily.againstAverage', { change: arrowWords(format, average.vsAverage), amount: format.money(average.average) })}
          </li>
        ) : null}
      </ul>
    </ReportCard>
  );
}

function Venues({ summary }: { readonly summary: DailySummary }): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const columns: readonly TableColumn<DailySummaryVenue>[] = [
    {
      key: 'venue',
      header: t('reports.daily.venue'),
      cell: (row) => (
        <span className="console-ds-venue">
          {row.name}
          {row.venueId === summary.bestVenueId ? (
            <Badge tone="ok" dot>
              {t('reports.daily.bestBadge')}
            </Badge>
          ) : null}
          {row.dayClosed ? null : (
            <Badge tone="warn" dot>
              {t('reports.daily.notClosed')}
            </Badge>
          )}
        </span>
      ),
    },
    { key: 'net', header: t('reports.daily.netTakings'), numeric: true, cell: (row) => <span className="console-money">{format.money(row.kpis.takings.netTakings)}</span> },
    { key: 'sessions', header: t('reports.daily.sessions'), numeric: true, cell: (row) => <span className="console-num">{format.number(row.kpis.play.sessions)}</span> },
    { key: 'occupancy', header: t('reports.daily.occupancy'), numeric: true, cell: (row) => <span className="console-num">{percentOf(format, row.kpis.play.utilisationBasisPoints)}</span> },
    {
      key: 'change',
      header: t('reports.daily.change'),
      numeric: true,
      cell: (row) => <span className="console-num">{arrowWords(format, row.moneyTrends.find((one) => one.metric === 'netTakings')?.vsLastWeek)}</span>,
    },
  ];
  return (
    <ReportCard title={t('reports.daily.venues')} description={t('reports.daily.venuesHint')} flush>
      <Table caption={t('reports.daily.venues')} captionHidden columns={columns} rows={summary.venues} rowKey={(row) => row.venueId} rowTone={(row) => (row.dayClosed ? undefined : 'warn')} empty={NO_DATA} />
    </ReportCard>
  );
}

function Sections({ kpis }: { readonly kpis: DailySummaryKpis }): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const amount = (value: Money) => format.money(value);
  const minus = (value: Money) => (value.amount === 0 ? format.money(value) : `−${format.money(value)}`);
  return (
    <ReportGrid layout="three">
      <ReportCard title={t('reports.daily.takings')} description={t('reports.daily.takingsHint')}>
        <ReportRows label={t('reports.daily.takings')}>
          <ReportRow label={t('reports.daily.grossSales')} value={amount(kpis.takings.grossSales)} />
          <ReportRow label={t('reports.daily.discounts')} value={minus(kpis.takings.discounts)} quiet={kpis.takings.discounts.amount === 0} />
          <ReportRow label={t('reports.daily.refunds', { count: kpis.takings.refundCount })} value={minus(kpis.takings.refunds)} quiet={kpis.takings.refunds.amount === 0} />
          <ReportRow label={t('reports.daily.tax')} value={amount(kpis.takings.tax)} quiet={kpis.takings.tax.amount === 0} />
          <ReportRow label={t('reports.daily.netTakings')} value={amount(kpis.takings.netTakings)} strong />
          <ReportRow label={t('reports.daily.moneyIn')} value={amount(kpis.takings.moneyIn)} />
        </ReportRows>
      </ReportCard>
      <ReportCard title={t('reports.daily.paidBy')} description={t('reports.daily.paidByHint')}>
        <ReportRows label={t('reports.daily.paidBy')}>
          {kpis.takings.byTender.map((line) => (
            <ReportRow key={line.tender} label={t(`reports.daily.tender.${line.tender}`)} value={amount(line.amount)} quiet={line.amount.amount === 0} />
          ))}
          <ReportRow label={t('reports.daily.onAccount')} value={amount(kpis.takings.onAccount)} quiet={kpis.takings.onAccount.amount === 0} />
        </ReportRows>
      </ReportCard>
      <ReportCard title={t('reports.daily.play')} description={t('reports.daily.playHint')}>
        <ReportRows label={t('reports.daily.play')}>
          <ReportRow label={t('reports.daily.sessions')} value={format.number(kpis.play.sessions)} />
          <ReportRow label={t('reports.daily.hoursPlayed')} value={hoursWords(t, format, kpis.play.playSeconds)} />
          <ReportRow label={t('reports.daily.averageSession')} value={kpis.play.averageSessionSeconds === null ? NO_DATA : hoursWords(t, format, kpis.play.averageSessionSeconds)} />
          <ReportRow label={t('reports.daily.occupancyOpen')} value={percentOf(format, kpis.play.utilisationBasisPoints)} />
          <ReportRow label={t('reports.daily.perStation')} value={kpis.play.revenuePerStation === null ? NO_DATA : amount(kpis.play.revenuePerStation)} />
          <ReportRow label={t('reports.daily.passSales')} value={amount(kpis.play.passSales)} quiet={kpis.play.passSales.amount === 0} />
          <ReportRow label={t('reports.daily.payAsYouGo')} value={amount(kpis.play.payAsYouGo)} quiet={kpis.play.payAsYouGo.amount === 0} />
          <ReportRow label={t('reports.daily.uniquePlayers')} value={format.number(kpis.players.unique)} />
          <ReportRow label={t('reports.daily.newPlayers')} value={format.number(kpis.players.new)} quiet={kpis.players.new === 0} />
        </ReportRows>
      </ReportCard>
      <ReportCard title={t('reports.daily.food')} description={t('reports.daily.foodHint')}>
        <ReportRows label={t('reports.daily.food')}>
          <ReportRow label={t('reports.daily.foodSales')} value={amount(kpis.food.foodSales)} quiet={kpis.food.foodSales.amount === 0} />
          <ReportRow label={t('reports.daily.retailSales')} value={amount(kpis.food.retailSales)} quiet={kpis.food.retailSales.amount === 0} />
          <ReportRow label={t('reports.daily.orders')} value={format.number(kpis.food.orders)} quiet={kpis.food.orders === 0} />
          {kpis.food.topItems.length === 0 ? (
            <ReportRow label={t('reports.daily.bestSellers')} value={t('reports.daily.noItems')} quiet />
          ) : (
            kpis.food.topItems.map((item, index) => (
              <ReportRow key={`${item.kind}:${item.name}`} label={t('reports.daily.topItem', { name: item.name, quantity: item.quantity })} hint={index === 0 ? t('reports.daily.bestSellers') : undefined} value={amount(item.net)} />
            ))
          )}
          {kpis.food.stockAlerts === null ? null : <ReportRow label={t('reports.daily.stockAlerts')} value={format.number(kpis.food.stockAlerts)} quiet={kpis.food.stockAlerts === 0} />}
        </ReportRows>
      </ReportCard>
      <ReportCard title={t('reports.daily.wallet')} description={t('reports.daily.walletHint')}>
        <ReportRows label={t('reports.daily.wallet')}>
          <ReportRow label={t('reports.daily.topUps')} value={amount(kpis.wallet.topUps)} quiet={kpis.wallet.topUps.amount === 0} />
          <ReportRow label={t('reports.daily.bonusIssued')} value={amount(kpis.wallet.bonusIssued)} quiet={kpis.wallet.bonusIssued.amount === 0} />
          <ReportRow label={t('reports.daily.spent')} value={amount(kpis.wallet.spent)} quiet={kpis.wallet.spent.amount === 0} />
          {kpis.wallet.onAccountOwed === null ? null : <ReportRow label={t('reports.daily.onAccountOwed')} value={amount(kpis.wallet.onAccountOwed)} />}
        </ReportRows>
      </ReportCard>
      <ReportCard title={t('reports.daily.cash')} description={t('reports.daily.cashHint')}>
        <ReportRows label={t('reports.daily.cash')}>
          {kpis.cash.expected === null ? null : <ReportRow label={t('reports.daily.expected')} value={amount(kpis.cash.expected)} />}
          {kpis.cash.counted === null ? null : <ReportRow label={t('reports.daily.counted')} value={amount(kpis.cash.counted)} />}
          <ReportRow label={t('reports.daily.variance')} value={amount(kpis.cash.variance)} strong />
          {kpis.cash.drawers.map((drawer) => (
            <ReportRow
              key={drawer.shiftId}
              label={drawer.counterName === null ? t('reports.daily.drawerUnnamed') : t('reports.daily.drawer', { counter: drawer.counterName })}
              value={drawer.status === 'open' || drawer.variance === null ? t('reports.daily.drawerOpen') : amount(drawer.variance)}
            />
          ))}
          {kpis.cash.openShifts > 0 ? <ReportRow label={t('reports.daily.openShifts')} value={format.number(kpis.cash.openShifts)} /> : null}
        </ReportRows>
      </ReportCard>
    </ReportGrid>
  );
}
